import type { DialogProps, SelectChangeEvent } from "@mui/material";
import type { ContentFlagContentType } from "src/types/content-flag";

import { toast } from "sonner";
import { useMemo, useState, useEffect } from "react";

import { Stack, Dialog, Button, Select, Checkbox, Skeleton, MenuItem, TextField, Typography, DialogTitle, ListItemText, DialogContent, DialogActions } from "@mui/material";

import { useCreateContentFlag, useGetContentFlagReasons, mutateEveryContentFlagEndpoint } from "src/actions/content-flag";

import { useAuthContext } from "src/auth/hooks";
import { hasSuperUserOrBetaAccess } from "src/auth/utils/permissions";

import { ContentTypeLabel } from "./content-type-label";


type NewContentFlagDialogProps = DialogProps & {
  contentType: ContentFlagContentType;
  guid: string;
  isRespondingToChange?: boolean;
  onClose: () => void;
}


export function NewContentFlagDialog({ contentType, guid, isRespondingToChange, onClose, open, ...other }: NewContentFlagDialogProps) {
  const { userProfile, loading: authLoading } = useAuthContext();
  const canAccess = hasSuperUserOrBetaAccess(userProfile);

  useEffect(() => {
    if (authLoading || !open) {
      return;
    }
    if (!canAccess) {
      onClose();
    }
  }, [authLoading, open, canAccess, onClose]);

  const { createContentFlag, isCreating, error: createError } = useCreateContentFlag(contentType, guid);
  const { contentFlagReasons, contentFlagReasonsLoading, contentFlagReasonsError } = useGetContentFlagReasons();

  const validReasonOptions = useMemo(() => contentFlagReasons.filter((contentFlagReason) => {
      if (contentFlagReason[contentType]) {
        return contentFlagReason
      } 
        return false;
      
    }), [contentFlagReasons, contentType]);

  const [reasons, setReasons] = useState<number[]>([]);
  const [body, setBody] = useState("");

  const isLoading = useMemo(() =>
    contentFlagReasonsLoading ||
    isCreating
    , [contentFlagReasonsLoading, isCreating]);

  const isError = useMemo(() =>
    contentFlagReasonsError ||
    createError
    , [contentFlagReasonsError, createError]);

  const errorMessage = typeof isError === 'string' ? isError : isError?.message;

  const handleReasonChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;

    setReasons(
      typeof value === 'string'
        ? value.split(',').filter(Boolean).map(Number)
        : value.map(Number)
    );
  };

  const handleCreateContentFlag = async () => {
    if (reasons.length === 0) {
      return;
    }
    try {
      await createContentFlag({
        reasons,
        body: body.trim() ? body.trim() : undefined,
      });
      toast.success('Content flag created successfully');
      mutateEveryContentFlagEndpoint();
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const formIncomplete = useMemo(() => reasons.length === 0, [reasons]);

  const handleClose = () => {
    setReasons([]);
    setBody("");
    onClose?.();
  };

  if (authLoading || !canAccess) {
    return null;
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={() => handleClose()} {...other}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">
            {isRespondingToChange ? 'Atlas content was changed' : 'New content flag'}
          </Typography>
          <ContentTypeLabel contentType={contentType} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isLoading ? <Skeleton variant="rectangular" height={100} /> : isError ? <Typography color="error">{errorMessage}</Typography> : null}
        {!isLoading && !isError && validReasonOptions.length === 0 ? <Typography>No content flags found</Typography> : null}
        {!isLoading && !isError && validReasonOptions.length > 0 ? (
          <Stack spacing={2}>
              <Typography variant="subtitle2">Reason</Typography>
              <Select
                multiple
                displayEmpty
                value={reasons}
                onChange={handleReasonChange}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>Select one or more reasons</em>;
                  }

                  return validReasonOptions
                    .filter((contentFlagReason) => selected.includes(contentFlagReason.id))
                    .map((contentFlagReason) => contentFlagReason.name)
                    .join(', ');
                }}
              >
                {validReasonOptions.map((contentFlagReason) => (
                  <MenuItem key={contentFlagReason.id} value={contentFlagReason.id}>
                    <Checkbox checked={reasons.includes(contentFlagReason.id)} />
                    <ListItemText primary={contentFlagReason.name} />
                  </MenuItem>
                ))}
              </Select>
              <>
                <Typography variant="subtitle2">Additional details (optional)</Typography>
                <TextField
                  value={body}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBody(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
              </>
            </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button color="error" variant="outlined" onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreateContentFlag} disabled={formIncomplete}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}